import { z } from "zod";
import type { Operation } from "../types";
import type { GoalLedgerItem } from "../../blocks/agents/goals-ledger";
import { agentGoalsListFiltersSchema } from "@excellent/core/op-schemas";

/**
 * `/agents/goals` contracts (RO-3-02 — the "big delegator" MID recipe, PRD
 * §11.3, §13 R5). Port of the server-side assembly `excellent/src/agents/
 * goals-home.ts` (`buildGoalsHome`) + `goal-create.ts` (`createGoalImpl`) +
 * `db.ts` (`getGoal`) did directly against the vault from an async Server
 * Component — this file is the contract those three become.
 *
 * Deliberately narrower than `buildGoalsHome`: that function also assembles
 * STRATEGIC goals (kind='strategic', the former Objectives) for the
 * consolidated Work ▸ Goals home. `/agents/goals` only ever renders the
 * agentic-scoped projection (`scope="agentic"` in the original `GoalsView`),
 * so `list` here returns agentic goals only — it does not need
 * `listObjectives`/`withLiveSignals`, which stay Work-section territory
 * (RO-3-03). When Work ▸ Goals is ported, its contract should WIDEN this
 * shape (add an optional `kind` filter) rather than fork a second one.
 *
 * RO-7-04: list filter atoms shared with goal_list MCP via op-schemas.
 */

export interface AvailableRoleDTO {
  name: string;
  description: string;
}

const listQuery = z.object({
  /** Narrow to goals scoped to these project ids (project-tier view). */
  projectIds: agentGoalsListFiltersSchema.shape.projectIds,
});
export type AgentGoalsListQuery = z.infer<typeof listQuery>;

const listOp: Operation<void, AgentGoalsListQuery, void, { goals: GoalLedgerItem[] }> = {
  method: "GET",
  path: () => "/api/agent-goals",
  query: listQuery,
  response: z.custom<{ goals: GoalLedgerItem[] }>(),
};

const rolesOp: Operation<void, void, void, { roles: AvailableRoleDTO[] }> = {
  method: "GET",
  path: () => "/api/agent-goals/roles",
  response: z.custom<{ roles: AvailableRoleDTO[] }>(),
};

const resolveOp: Operation<{ id: number }, void, void, { uid: string | null }> = {
  method: "GET",
  path: ({ id }) => `/api/agent-goals/${id}/resolve`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: z.object({ uid: z.string().nullable() }),
};

const createBody = z.object({
  title: z.string(),
  outcome: z.string().optional(),
  why: z.string().optional(),
  role: z.string().optional(),
  cadence: z.string().optional(),
  project: z.string().optional(),
  description: z.string().optional(),
  task: z.string().optional(),
  lane: z.string().optional(),
});
export type AgentGoalCreateBody = z.infer<typeof createBody>;

export type AgentGoalCreateResult =
  | { ok: true; id: number; slug: string; uid: string | null }
  | { ok: false; reason: "title-required" | "unknown-role" | "project-not-found"; detail?: string };

const createOp: Operation<void, void, AgentGoalCreateBody, AgentGoalCreateResult> = {
  method: "POST",
  path: () => "/api/agent-goals",
  body: createBody,
  response: z.custom<AgentGoalCreateResult>(),
};

/**
 * RO-2-09 — port of `excellent/src/agents/goal-actions.ts`'s `defineGoal`, a
 * `"use server"` action with no route.ts equivalent. Writes the goal's OUTCOME
 * + DEFINITION OF DONE (v83 additive columns) directly — the form submits full
 * current state, so this overwrites all definition fields; a blank input clears
 * the field (NULL). The Next `/agents/goals/[id]/define` page still posts to the
 * original server action (a native `<form action>`, no client JS) — repointing
 * that call site is a page-level (L3) change, not an action-level one; this
 * endpoint is the correct wiring for when that page is ported.
 */
const defineBody = z.object({
  outcome: z.string().nullable().optional(),
  why: z.string().nullable().optional(),
  metric_label: z.string().nullable().optional(),
  metric_baseline: z.string().nullable().optional(),
  metric_current: z.string().nullable().optional(),
  metric_target: z.string().nullable().optional(),
  metric_pct: z.union([z.number(), z.string()]).nullable().optional(),
  /** Textarea convention: one criterion per line, `[x]`/`[ ]` marks done. */
  criteria: z.string().nullable().optional(),
  /** Textarea convention: one milestone per line, `[x]`/`[>]`/`[ ]` + optional ` | hint`. */
  milestones: z.string().nullable().optional(),
});
export type AgentGoalDefineBody = z.infer<typeof defineBody>;

const defineOp: Operation<{ id: number }, void, AgentGoalDefineBody, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/define`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  body: defineBody,
  response: z.object({ ok: z.literal(true) }),
};

/* ──────────────────────────────────────────────────────────────────────────────
 * RO-2-11 — the goal LIFECYCLE half of `excellent/src/agents/actions.ts`, the
 * `other` action domain's largest single file (8 of its 9 actions land here; the
 * 9th, `spawnWorkerNow`, is not goal-scoped and lives on `./agents`).
 *
 * All eight are `"use server"` actions with no `route.ts` equivalent anywhere in
 * the tree — the operator controls behind /agents' goal buttons. Each keeps its
 * source action's EXACT return shape, including the discriminated `reason`
 * strings the UI switches on, so the eventual client cutover is a call-site swap
 * and not a re-modelling. The source actions each call `revalidatePath` on up to
 * four surfaces; endpoints cannot, so those targets are recorded per-route in
 * `excellent/server/routes/agent-goals.ts` for the L5 invalidation graph (§13 R2)
 * rather than declared as `invalidates` here — that mechanism keys on API cache
 * keys, and none of the four Next surfaces has a contract yet.
 *
 * Live Next call sites are deliberately LEFT on their server actions
 * (`ccb89192`'s ruling: a Hono-only route 404s under Next while both stacks run).
 * ────────────────────────────────────────────────────────────────────────────── */

const idParams = z.object({ id: z.coerce.number().int().positive() });

/** `pauseGoal` / `resumeGoal` — a bare status flip; refuses from the wrong state. */
export type AgentGoalStatusResult = { ok: boolean };

const pauseOp: Operation<{ id: number }, void, void, AgentGoalStatusResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/pause`,
  params: idParams,
  response: z.object({ ok: z.boolean() }),
};

const resumeOp: Operation<{ id: number }, void, void, AgentGoalStatusResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/resume`,
  params: idParams,
  response: z.object({ ok: z.boolean() }),
};

/**
 * `abandonGoal` / `completeGoal` — the two terminal HAND closes. The `note` is
 * REQUIRED by the source actions (`{ ok:false, reason:"note-required" }` on a
 * blank one) because both stamp who/why into the goal description; the zod body
 * keeps it a plain `string` rather than `.min(1)` so the handler reproduces that
 * refusal as a 200 envelope, exactly as the action did, instead of a 400.
 */
const noteBody = z.object({ note: z.string() });
export type AgentGoalNoteBody = z.infer<typeof noteBody>;

export type AgentGoalTerminalResult = {
  ok: boolean;
  reason?: "note-required" | "goal-not-found" | "already-terminal";
};

const terminalResponse = z.object({
  ok: z.boolean(),
  reason: z.enum(["note-required", "goal-not-found", "already-terminal"]).optional(),
});

const abandonOp: Operation<{ id: number }, void, AgentGoalNoteBody, AgentGoalTerminalResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/abandon`,
  params: idParams,
  body: noteBody,
  response: terminalResponse,
};

const completeOp: Operation<{ id: number }, void, AgentGoalNoteBody, AgentGoalTerminalResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/complete`,
  params: idParams,
  body: noteBody,
  response: terminalResponse,
};

/**
 * `reconcileGoalsNow` — the "Reconcile now" button: one pass over every active
 * goal, auto-closing those whose linked issues are all terminal. No params and no
 * body; the counts come back so the UI can say what happened.
 */
export type AgentGoalReconcileResult = {
  ok: boolean;
  closed: number;
  ready: number;
  reason?: string;
};

const reconcileOp: Operation<void, void, void, AgentGoalReconcileResult> = {
  method: "POST",
  path: () => "/api/agent-goals/reconcile",
  response: z.object({
    ok: z.boolean(),
    closed: z.number().int(),
    ready: z.number().int(),
    reason: z.string().optional(),
  }),
};

/**
 * `verifyAndCloseGoal` — the EVIDENCE-based close for one goal (closes iff every
 * linked issue is done). `reason` is free-form `string`, not an enum: the source
 * action returns either the literal `"not-ready"` or the reconcile CLI's own
 * error message, and narrowing it here would silently drop the latter.
 */
export type AgentGoalVerifyCloseResult = { ok: boolean; reason?: string };

const verifyCloseOp: Operation<{ id: number }, void, void, AgentGoalVerifyCloseResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/verify-close`,
  params: idParams,
  response: z.object({ ok: z.boolean(), reason: z.string().optional() }),
};

/**
 * `decomposeGoalWithPlanner` — spawns the `planner` role scoped to this goal so
 * it writes concrete issues. DETACHED: `ok:true` means "spawned", never
 * "finished"; `locked` means a planner is already running.
 */
export type AgentGoalDecomposeResult =
  | { ok: true }
  | { ok: false; reason: "locked" | "spawn-failed"; detail?: string };

const decomposeOp: Operation<{ id: number }, void, void, AgentGoalDecomposeResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/decompose`,
  params: idParams,
  response: z.custom<AgentGoalDecomposeResult>(),
};

/**
 * `promoteGoalNow` — the scaled-agents human gate: merge this goal's team branch
 * (`agents/team-<id>`) onto the base branch via `excellent-mcp agent promote
 * --confirm`. `message` is pre-composed prose (the source action assembles the
 * blocker list into it), so the contract carries it verbatim rather than
 * re-modelling blockers into a structured field the UI does not have.
 */
export type AgentGoalPromoteResult = { ok: boolean; message: string };

const promoteOp: Operation<{ id: number }, void, void, AgentGoalPromoteResult> = {
  method: "POST",
  path: ({ id }) => `/api/agent-goals/${id}/promote`,
  params: idParams,
  response: z.object({ ok: z.boolean(), message: z.string() }),
};

/**
 * RO-3-13 — the READ half of the same page, which RO-2-09 could not add because
 * it was porting an ACTION, not a page.
 *
 * `defineOp` above notes that "the Next `/agents/goals/[id]/define` page still
 * posts to the original server action ... this endpoint is the correct wiring
 * for when that page is ported." This is that port, and it needed one thing the
 * action port did not: the form is a `defaultValue` form over the goal's
 * CURRENT definition, so the client route must be able to read the nine
 * definition fields back before it can render a single input.
 *
 * ## Decision — a definition-scoped DTO, not the whole `GoalRow`
 *
 * `getGoal()` returns `GoalRow`, ~30 columns including `lane_uid`, cadence,
 * status and role wiring. This publishes the nine fields the form writes, plus
 * `title` (the page's description line) and `id`. The symmetry is deliberate
 * and checkable: every field here has a counterpart in `defineBody`, so a
 * reviewer can see at a glance that the form round-trips exactly what it reads
 * and nothing more. Widening this DTO without widening `defineBody` would mean
 * rendering a field the Save button silently discards.
 *
 * `detail: null` carries not-found rather than a 404 — the original page called
 * `notFound()`, and the client route renders its own not-found state with the
 * back-link intact, matching the convention RO-3-02b set for this namespace.
 *
 * ## W4-A1 UX (#44604) — `drafts`
 *
 * When `criteria` is empty, the server DERIVES draft acceptance criteria from
 * the goal's outcome, metric, linked tasks, and those tasks' existing DoD /
 * gates (RECURSIVE-DONE §13 / §6.5 — detect-don't-ask). Each draft carries a
 * provenance `why` for the confirm UI. Drafts are proposals only; confirm still
 * POSTs through `define` and dual-writes `source='authored'` via #44603.
 * When `criteria` is already authored, `drafts` is `[]` and the form shows the
 * saved checklist.
 */
export interface AgentGoalDraftCriterion {
  label: string;
  why: string;
  origin: "outcome" | "metric" | "task" | "task_dod" | "gate";
}

export interface AgentGoalDefinition {
  id: number;
  title: string;
  outcome: string | null;
  why: string | null;
  metric_label: string | null;
  metric_baseline: string | null;
  metric_current: string | null;
  metric_target: string | null;
  metric_pct: number | null;
  /** Stored JSON. The client serializes it to the `[x]`/`[ ]` line convention. */
  criteria: string | null;
  /** Stored JSON. The client serializes it to the `[x]`/`[>]`/`[ ]` convention. */
  milestones: string | null;
  /**
   * Detect-don't-ask proposals when criteria are empty. Empty array when the
   * goal already has an authored checklist.
   */
  drafts: AgentGoalDraftCriterion[];
}

const definitionOp: Operation<
  { id: number },
  void,
  void,
  { detail: AgentGoalDefinition | null }
> = {
  method: "GET",
  path: ({ id }) => `/api/agent-goals/${id}/define`,
  params: z.object({ id: z.coerce.number().int().positive() }),
  response: z.custom<{ detail: AgentGoalDefinition | null }>(),
};

export const agentGoals = {
  list: listOp,
  roles: rolesOp,
  resolve: resolveOp,
  create: createOp,
  define: defineOp,
  // RO-2-11 — the 8 goal-lifecycle actions from src/agents/actions.ts.
  pause: pauseOp,
  resume: resumeOp,
  abandon: abandonOp,
  complete: completeOp,
  reconcile: reconcileOp,
  verifyClose: verifyCloseOp,
  decompose: decomposeOp,
  promote: promoteOp,
  definition: definitionOp,
};
